import React, { useRef, useState, useEffect, useMemo } from "react";
import { range } from "d3-array";
import { scaleLinear } from "d3-scale";
import { useHintVisibility } from "../hooks/useHintVisibility";

interface AppleRingProps {
  budgeted: number;
  spent: number;
  adultMode: boolean;
  radius?: number;
  strokeWidth?: number;
  position: { x: number; y: number };
  /**
   * Overall opacity for the ring container. Defaults to 0.75 so the cat remains visually prominent.
   */
  opacity?: number;
  /** When true, ring plays a subtle pulsing animation */
  pulse?: boolean;
}

// ---------- Helper utilities (lifted from sample) ----------
function generateStableId() {
  return Math.random().toString(16).split(".")[1];
}

function partitionOnce(total: number, part: number): [number, number] {
  return total >= part ? [part, total - part] : [total, 0];
}

function interpolateColor(colors: string[]) {
  const length = colors.length;
  const domain = range(length).map((i) => i / (length - 1));
  // @ts-ignore d3 typings are broad enough
  return scaleLinear<string>().domain(domain).range(colors);
}

function cumulate(values: number[]) {
  let from = 0;
  return values.map((value) => ({ value, from, to: (from += value) }));
}

function computeGradientRotation(angle: number) {
  return {
    x1: Math.cos(angle) * 0.5 + 0.5,
    y1: Math.sin(angle) * 0.5 + 0.5,
    x2: Math.cos(angle - Math.PI) * 0.5 + 0.5,
    y2: Math.sin(angle - Math.PI) * 0.5 + 0.5,
  } as const;
}

// ---------- Core ring component ----------
const RingSVG: React.FC<{
  angle: number;
  radius: number;
  strokeWidth: number;
  cx: number;
  cy: number;
  colors: string[];
  gradientStartId: string;
  gradientEndId: string;
  gradientShadowId: string;
  maskStartId: string;
  maskEndId: string;
}> = ({
  angle,
  radius,
  strokeWidth,
  cx,
  cy,
  colors,
  gradientStartId,
  gradientEndId,
  gradientShadowId,
  maskStartId,
  maskEndId,
}) => {
  const initialPhase = -Math.PI / 2; // start at top
  const partitionStep = Math.PI; // split when > 180deg for arc sweep flag

  const singleLapAngle = Math.min(angle, Math.PI * 2);
  const ringRotationAngle = angle - singleLapAngle;
  const phase = initialPhase + ringRotationAngle;

  const partitionAngles = partitionOnce(singleLapAngle, partitionStep);
  const [segmentStart, segmentEnd] = cumulate(partitionAngles);

  const colorInterp = interpolateColor(colors.slice(0, 3));

  const arcX = (a: number) => Math.cos(a + phase) * radius + cx;
  const arcY = (a: number) => Math.sin(a + phase) * radius + cy;

  const buildPath = (seg: { from: number; to: number; value: number }) =>
    `M ${arcX(seg.from)} ${arcY(seg.from)} A ${radius} ${radius} 0 ${
      seg.value > Math.PI ? 1 : 0
    } 1 ${arcX(seg.to)} ${arcY(seg.to)}`;

  // Gradient rotation for trailing shadow when >100%
  const gradientAngle = Math.max(0, angle - Math.PI * 2) + initialPhase;
  const gradientRotation = computeGradientRotation(gradientAngle);
  const gradientSide = radius * 2 + strokeWidth;

  return (
    <g>
      <defs>
        <linearGradient id={gradientStartId} {...gradientRotation}>
          <stop offset="15%" stopColor={colorInterp(0)} />
          <stop offset="85%" stopColor={colorInterp(0.5)} />
        </linearGradient>
        <linearGradient id={gradientEndId} {...gradientRotation}>
          <stop offset="15%" stopColor={colorInterp(1)} />
          <stop offset="85%" stopColor={colorInterp(0.5)} />
        </linearGradient>
        <radialGradient id={gradientShadowId}>
          <stop offset="40%" stopColor="black" />
          <stop offset="100%" stopColor="black" stopOpacity={0} />
        </radialGradient>
        <mask id={maskStartId}>
          <path
            d={buildPath(segmentStart)}
            strokeWidth={strokeWidth}
            stroke="white"
            fill="none"
            strokeLinecap="round"
          />
        </mask>
        <mask id={maskEndId}>
          <path
            d={buildPath(segmentEnd)}
            strokeWidth={strokeWidth}
            stroke="white"
            fill="none"
            strokeLinecap="round"
          />
        </mask>
      </defs>

      {/* background faint ring */}
      <circle
        cx={cx}
        cy={cy}
        r={radius}
        fill="none"
        stroke={colorInterp(0.5)}
        opacity={0.25}
        strokeWidth={strokeWidth}
      />

      {/* leading arc (0–180deg) */}
      <rect
        x={cx - gradientSide / 2}
        y={cy - gradientSide / 2}
        width={gradientSide}
        height={gradientSide}
        fill={`url(#${gradientStartId})`}
        mask={`url(#${maskStartId})`}
      />

      {/* shadow blob at end for >270deg */}
      <circle
        r={strokeWidth / 2 + 6}
        cx={arcX(segmentEnd.to)}
        cy={arcY(segmentEnd.to)}
        fill={`url(#${gradientShadowId})`}
        mask={`url(#${maskStartId})`}
        opacity={angle > Math.PI * 1.5 ? 1 : 0}
      />

      {/* trailing arc if angle ≥ 180deg */}
      {angle >= Math.PI && (
        <rect
          x={cx - gradientSide / 2}
          y={cy - gradientSide / 2}
          width={gradientSide}
          height={gradientSide}
          fill={`url(#${gradientEndId})`}
          mask={`url(#${maskEndId})`}
        />
      )}
    </g>
  );
};

// ---------- Public component ----------
export const AppleRing: React.FC<AppleRingProps> = ({
  budgeted,
  spent,
  adultMode, // not used in this visual but kept for interface consistency
  radius = 90,
  strokeWidth = 9,
  position,
  opacity = 0.75,
  pulse = false,
}) => {
  const { showAppleRing } = useHintVisibility();

  // progress ratio – allow >1 for overshoot visuals (spent / budgeted)
  const targetProgress = spent > 0 ? budgeted / spent : 2;

  // --- smooth tweening between updates ---
  const [animatedProgress, setAnimatedProgress] = useState(targetProgress);
  const progressRef = useRef(targetProgress); // always tracks latest displayed progress
  const rafRef = useRef<number>();

  // Keep mutable ref in sync with displayed progress
  useEffect(() => {
    progressRef.current = animatedProgress;
  }, [animatedProgress]);

  useEffect(() => {
    const startValue = progressRef.current; // start from what's currently on-screen
    const endValue = targetProgress;
    if (startValue === endValue) return; // no change

    const duration = 600; // ms – slightly faster for snappier response
    const startTime = performance.now();

    const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

    const step = (now: number) => {
      const elapsed = now - startTime;
      const t = Math.min(elapsed / duration, 1);
      const eased = easeOutCubic(t);
      setAnimatedProgress(startValue + (endValue - startValue) * eased);
      if (t < 1) {
        rafRef.current = requestAnimationFrame(step);
      } else {
        progressRef.current = endValue;
      }
    };

    rafRef.current && cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(step);

    return () => {
      rafRef.current && cancelAnimationFrame(rafRef.current);
    };
  }, [targetProgress]);

  // Colours depend on financial status
  let colors: string[];
  if (spent > budgeted) {
    // Over-budget – magenta gradient
    colors = ["#e63754", "#e64c85"];
  } else if (spent < budgeted) {
    // Surplus – vivid green
    colors = ["#36d842", "#9bff00"];
  } else {
    // Exactly balanced – calm blue
    colors = ["#60d6fa", "#75fbb0"];
  }

  const outerRadius = radius + strokeWidth / 2; // Actual outer edge of the ring
  const size = outerRadius * 2; // Container should match actual ring size
  const cx = size / 2;
  const cy = size / 2;

  const angle = animatedProgress * Math.PI * 2; // may exceed 2π

  // Generate stable IDs once for this component instance
  const ids = useMemo(
    () => ({
      gradientStartId: generateStableId(),
      gradientEndId: generateStableId(),
      gradientShadowId: generateStableId(),
      maskStartId: generateStableId(),
      maskEndId: generateStableId(),
    }),
    [],
  );

  // --- Opacity handling --------------------------------------------------
  // We want to show the final blue ring briefly, then fade it away so the UI
  // looks clean.  Manage opacity with internal state + CSS transition.
  const [ringOpacity, setRingOpacity] = useState(opacity);

  useEffect(() => {
    if (spent === budgeted) {
      // Show blue ring for 1000 ms, then fade.
      setRingOpacity(opacity); // ensure fully visible first

      const id = setTimeout(() => setRingOpacity(0), 1000);
      return () => clearTimeout(id);
    } else {
      // Not balanced – ensure ring is visible
      setRingOpacity(opacity);
    }
  }, [spent, budgeted, opacity]);

  const containerStyle: React.CSSProperties = {
    position: "absolute",
    left: position.x - size / 2,
    top: position.y - size / 2,
    width: size,
    height: size,
    pointerEvents: "fill",
    cursor: "pointer",
    clipPath: "circle(50%)",
    opacity: ringOpacity,
    transition: "opacity 0.4s ease-in-out",
    zIndex: 0,
    transformOrigin: "center center",
    ...(pulse ? { animation: "pulse 1.4s ease-in-out infinite" } : {}),
  };

  // If apple ring should be hidden and not pulsing (selected), don't render anything
  // Always show the ring when selected (pulse = true) for transfer feedback
  if (!showAppleRing && !pulse) {
    return null;
  }

  return (
    <div style={containerStyle}>
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        style={{ overflow: "visible" }}
      >
        <RingSVG
          angle={angle}
          radius={radius}
          strokeWidth={strokeWidth}
          cx={cx}
          cy={cy}
          colors={colors}
          {...ids}
        />
      </svg>
    </div>
  );
};
